// Colab brand mark — "The Open Node".
//
// An open "C" ring (ink, single stroke, round terminals) that never quite
// closes, opening to a solid terracotta node at the upper-right — a connection
// reaching out to a partner. Drawn on the design's 64-unit grid, 6-unit stroke.
//
// The ring stroke is `currentColor` so it follows the surrounding ink and flips
// automatically in dark mode; the node carries the one brand accent
// (--brand-terracotta). The wordmark is the editorial serif "Colab." with a
// terracotta period, per the Steep brand lockup.

// Geometry from openNode(cx=32, cy=32, r=18): the gap opens to the right, the
// node sits on the top-right terminal.
const RING_PATH = "M43.08 17.82 A18 18 0 1 0 43.08 46.18";
const NODE = { cx: 43.08, cy: 17.82 } as const;

/** Bare Open Node mark. Stroke = currentColor, node = terracotta. */
export function LogoMark({
  className = "",
  title = "Colab",
}: {
  className?: string;
  title?: string;
}) {
  return (
    <svg
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      className={className}
      fill="none"
    >
      <path
        d={RING_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth={6}
        strokeLinecap="round"
      />
      <circle cx={NODE.cx} cy={NODE.cy} r={5.52} fill="hsl(var(--brand-terracotta))" />
    </svg>
  );
}

/**
 * App-icon form — the node housed in an ink rounded tile (off-white stroke,
 * terracotta node). Legible down to 16px; mirrors the favicon.
 */
export function LogoTile({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-[24%] bg-primary text-primary-foreground ${className}`}
    >
      <LogoMark className="h-[62%] w-[62%]" />
    </span>
  );
}

/** Lockup — bare mark + serif "Colab." wordmark. */
export function Logo({
  className = "",
  showWordmark = true,
}: {
  className?: string;
  showWordmark?: boolean;
}) {
  return (
    <span className={`inline-flex items-center gap-2 text-foreground ${className}`}>
      <LogoMark className="h-7 w-7 shrink-0" />
      {showWordmark && (
        <span className="font-serif text-[19px] font-normal leading-none tracking-[-0.02em]">
          Colab<span className="text-brand-terracotta">.</span>
        </span>
      )}
    </span>
  );
}
